import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Waypoints } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Register() {
  const [form, setForm] = useState({ name: "", business_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/register", form);
      await refresh();
      toast.success("Welcome aboard!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="register-page">
      <div className="hidden lg:flex flex-col justify-between bg-neutral-900 text-white p-16">
        <Link to="/" className="flex items-center gap-2" data-testid="register-logo">
          <Waypoints className="h-5 w-5" />
          <span className="font-heading font-bold">CreateForms.app</span>
        </Link>
        <div>
          <h2 className="text-4xl font-black leading-tight">Start your first form in minutes.</h2>
          <p className="mt-4 text-neutral-400 max-w-md">Free plan included. Collect uploads, reply in threads, keep customers in the loop automatically.</p>
        </div>
        <p className="text-xs text-neutral-500">CreateForms.app — Customer Journey Management</p>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <div>
            <h1 className="text-3xl font-black">Create your account</h1>
            <p className="text-sm text-neutral-500 mt-2">You'll land on the free plan — upgrade anytime.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">Your name</Label>
            <Input id="name" required value={form.name} onChange={set("name")} className="h-12" data-testid="register-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="business">Business name</Label>
            <Input id="business" required value={form.business_name} onChange={set("business_name")} className="h-12" data-testid="register-business-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={form.email} onChange={set("email")} className="h-12" data-testid="register-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="h-12" data-testid="register-password-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full h-12" data-testid="register-submit-button">
            {loading ? "Creating…" : "Create account"}
          </Button>
          <p className="text-sm text-neutral-500">
            Already registered? <Link to="/login" className="text-neutral-900 font-medium underline" data-testid="register-login-link">Log in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
